import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../../../api/http";
import {
  fetchAvailableShifts,
  fetchStaffHome,
  fetchStaffShift,
  type StaffHome,
  type StaffShiftSummary,
} from "../../../api/staffApi";
import { ActiveShiftCard } from "../components/ActiveShiftCard";
import { EmptyState } from "../components/EmptyState";
import { ShiftCard } from "../components/ShiftCard";
import { SkeletonCard } from "../components/SkeletonCard";
import { StaffAlert } from "../components/StaffAlert";
import { StaffAppShell } from "../components/StaffAppShell";
export function StaffHomePage() {
  const [home, setHome] = useState<StaffHome | null>(null);
  const [currentEnriched, setCurrentEnriched] = useState<StaffShiftSummary | null>(null);
  const [openShifts, setOpenShifts] = useState<StaffShiftSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [homeData, available] = await Promise.all([fetchStaffHome(), fetchAvailableShifts()]);
        if (cancelled) return;
        setHome(homeData);
        setOpenShifts(available.slice(0, 3));
        if (homeData.currentShift) {
          const detail = await fetchStaffShift(homeData.currentShift.id);
          if (!cancelled) setCurrentEnriched(detail);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : "Could not load home.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <StaffAppShell>
      <div className="staff-page">
        <p className="staff-page-eyebrow">Staff</p>
        <h2 className="staff-page-title">Home</h2>

        {error ? <StaffAlert onDismiss={() => setError(null)}>{error}</StaffAlert> : null}

        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : home ? (
          <>
            <ActiveShiftCard home={home} enriched={currentEnriched} />

            {home.currentTask ? (
              <section className="staff-section">
                <h3 className="staff-section__title">Current task</h3>
                <Link to="/staff/work" className="staff-task-chip">
                  <span>{home.currentTask.title}</span>
                  <span className="muted">{home.currentTask.status}</span>
                </Link>
              </section>
            ) : null}

            {openShifts.length > 0 ? (
              <section className="staff-section">
                <div className="staff-section__head">
                  <h3 className="staff-section__title">Open shifts</h3>
                  <Link to="/staff/shifts" className="staff-inline-link">See all</Link>
                </div>
                <div className="staff-card-stack">
                  {openShifts.map((shift) => (
                    <ShiftCard key={shift.id} shift={shift} to={`/staff/shifts/${shift.id}`} />
                  ))}
                </div>
              </section>
            ) : (
              <EmptyState title="No open shifts right now" detail="Check back later or update your availability." />
            )}
          </>
        ) : null}
      </div>
    </StaffAppShell>
  );
}
